import { useEffect, useState } from 'react';
import { Inbox, ChevronLeft, Check } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import ProhoditSmenuModal from '../../ProhoditSmenuModal';
import { SECTION_COLOR, SECTION_BG, hlavicky } from '../komunikace';
import { DRUHY, kc } from './zadostiUi';
import PrilohyVyber from '../prilohy/PrilohyVyber';

/**
 * Nová žádost — `ModalShell`: nejdřív druh, pak jeho formulář.
 * Volno a nemoc zakládají nepřítomnost, výměna směny otevře stávající
 * modal prohození (tok Směn), záloha / nákup / jiné jdou do `stg_zadosti`.
 */
const NABIDKA = ['volno', 'nemoc', 'vymena', 'zaloha', 'nakup', 'jine'];
const POPIS = {
    volno: 'Dovolená nebo osobní volno — schvaluje nadřízený.',
    nemoc: 'Nahlásí se hned, neschvaluje se. Vedoucí uvidí, které směny odpadají.',
    vymena: 'Vyměnit nebo předat směnu kolegovi.',
    zaloha: 'Záloha ze mzdy — schvaluje vedení.',
    nakup: 'Pomůcky, vybavení, oblečení…',
    jine: 'Cokoli dalšího, o čem má rozhodnout nadřízený.',
};

export default function NovaZadost({ user, isDark, pocatecniDruh = null, onClose, onHotovo }) {
    const [druh, setDruh] = useState(pocatecniDruh);
    const [typVolna, setTypVolna] = useState('dovolena');
    const dnes = new Date().toISOString().slice(0, 10);
    const [od, setOd] = useState(dnes);
    const [doD, setDoD] = useState(dnes);
    // Nepřítomnost se vykazuje v hodinách — hodin za každý den rozsahu.
    const [hodiny, setHodiny] = useState('');
    const hod = Number(String(hodiny).replace(',', '.'));
    const [nazev, setNazev] = useState('');
    const [popis, setPopis] = useState('');
    const [castka, setCastka] = useState('');
    const [datum, setDatum] = useState('');
    const [ukladam, setUkladam] = useState(false);
    // Účtenka, fotka toho, co je potřeba koupit, potvrzení… (záloha, nákup, jiné).
    const [prilohy, setPrilohy] = useState([]);
    const [nahravam, setNahravam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [vysledekNemoci, setVysledekNemoci] = useState(null);
    const [smeny, setSmeny] = useState(null);
    const [smenaKVymene, setSmenaKVymene] = useState(null);

    useEffect(() => {
        if (druh !== 'vymena' || smeny !== null || !user?.zamestnanec_id) return;
        fetch(`/api/shifts/zamestnance/${user.zamestnanec_id}/budouci`).then(r => (r.ok ? r.json() : [])).then(d => setSmeny(Array.isArray(d) ? d : [])).catch(() => setSmeny([]));
    }, [druh, smeny, user]);

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const pole = { width: '100%', height: 42, borderRadius: 8, padding: '0 12px', outline: 'none', fontSize: 15, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light' };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '14px 0 6px' };
    const volba = (on) => ({ height: 38, padding: '0 12px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', background: on ? SECTION_BG : 'transparent', border: `1px solid ${on ? SECTION_COLOR : hrana}`, color: on ? SECTION_COLOR : 'var(--text-secondary)' });

    const odeslat = async () => {
        setUkladam(true);
        setChyba(null);
        let url, telo;
        if (druh === 'volno') { url = '/api/me/nedostupnosti'; telo = { od, do: doD, hodiny: hod, typ: typVolna, poznamka: popis || null }; }
        else if (druh === 'nemoc') { url = '/api/zadosti/nemoc'; telo = { od, do: doD, hodiny: hod, poznamka: popis || null }; }
        else { url = '/api/zadosti'; telo = { druh, nazev: nazev || null, popis: popis || null, castka: castka ? Number(castka) : null, datum: datum || null, prilohy: prilohy.map(p => p.id) }; }
        try {
            const res = await fetch(url, { method: 'POST', headers: hlavicky(), body: JSON.stringify(telo) });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) {
                const prvniChyba = d.errors ? Object.values(d.errors)[0]?.[0] : null;
                setChyba(d.error || prvniChyba || d.message || 'Nepodařilo se odeslat.');
                return;
            }
            if (druh === 'nemoc') { setVysledekNemoci(d); return; }
            onHotovo();
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
        } finally {
            setUkladam(false);
        }
    };

    const lze = !ukladam && !nahravam && (
        druh === 'volno' || druh === 'nemoc' ? (od && doD && doD >= od && hod >= 0.5 && hod <= 24)
            : druh === 'zaloha' ? Number(castka) > 0
                : (druh === 'nakup' || druh === 'jine') ? nazev.trim().length > 0 : false);

    if (smenaKVymene) {
        return <ProhoditSmenuModal smena={smenaKVymene} isDark={isDark} onClose={() => setSmenaKVymene(null)} onSuccess={() => { setSmenaKVymene(null); onHotovo(); }} />;
    }

    const nadpis = vysledekNemoci ? 'Nemoc nahlášena' : druh ? DRUHY[druh].nazev : 'Nová žádost';
    const paticka = vysledekNemoci ? (
        <button type="button" onClick={onHotovo} className="w-full rounded-xl font-semibold" style={{ height: 46, background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>Hotovo</button>
    ) : druh && druh !== 'vymena' ? (
        <div className="flex items-center gap-2">
            {chyba && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
            <button type="button" onClick={odeslat} disabled={!lze} className="rounded-lg font-semibold ml-auto"
                style={{ height: 44, padding: '0 20px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: lze ? 'pointer' : 'not-allowed', opacity: lze ? 1 : 0.45 }}>
                {ukladam ? 'Odesílám…' : druh === 'nemoc' ? 'Nahlásit' : 'Odeslat žádost'}
            </button>
        </div>
    ) : null;

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={nadpis} ikona={druh ? DRUHY[druh].Icon : Inbox} barva={SECTION_COLOR} sirka={500} paticka={paticka}>
            <div style={{ padding: '0 16px 16px' }}>
                {!druh && (
                    <div className="flex flex-col" style={{ gap: 8, marginTop: 12 }}>
                        {NABIDKA.map(d => {
                            const { nazev: n, Icon } = DRUHY[d];
                            return (
                                <button key={d} type="button" onClick={() => setDruh(d)} className="flex items-center text-left"
                                    style={{ gap: 12, minHeight: 58, padding: '8px 12px', borderRadius: 10, border: `1px solid ${hrana}`, background: 'transparent', cursor: 'pointer' }}>
                                    <span style={{ width: 36, height: 36, borderRadius: 9, background: SECTION_BG, color: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={18} /></span>
                                    <span>
                                        <span style={{ display: 'block', fontWeight: 700, fontSize: 14.5, color: 'var(--text-primary)' }}>{n}</span>
                                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{POPIS[d]}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {druh && !pocatecniDruh && !vysledekNemoci && (
                    <button type="button" onClick={() => { setDruh(null); setChyba(null); }} className="flex items-center gap-1 text-sm"
                        style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}>
                        <ChevronLeft size={15} /> Jiný druh žádosti
                    </button>
                )}

                {(druh === 'volno' || druh === 'nemoc') && !vysledekNemoci && (
                    <>
                        {druh === 'volno' && (
                            <>
                                <p style={popisek}>Druh volna</p>
                                <div className="flex flex-wrap gap-2">
                                    {[['dovolena', 'Dovolená'], ['osobni', 'Osobní volno'], ['jine', 'Jiné']].map(([k, t]) => (
                                        <button key={k} type="button" style={volba(typVolna === k)} onClick={() => setTypVolna(k)}>{t}</button>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div><p style={popisek}>Od</p><input type="date" value={od} onChange={e => { setOd(e.target.value); if (doD < e.target.value) setDoD(e.target.value); }} style={pole} /></div>
                            <div><p style={popisek}>Do</p><input type="date" value={doD} min={od} onChange={e => setDoD(e.target.value)} style={pole} /></div>
                        </div>
                        <p style={popisek}>Hodin za den</p>
                        <input type="number" inputMode="decimal" min="0.5" max="24" step="0.5" value={hodiny}
                            onChange={e => setHodiny(e.target.value)} placeholder="např. 8" style={pole} />
                        <p style={popisek}>Poznámka (nepovinné)</p>
                        <textarea value={popis} onChange={e => setPopis(e.target.value)} rows={3} style={{ ...pole, height: 'auto', padding: '10px 12px' }}
                            placeholder={druh === 'nemoc' ? 'Např. neschopenka od lékaře zítra' : 'Např. svatba sestry'} />
                        {druh === 'nemoc' && (
                            <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                                Nemoc se neschvaluje — hned se zapíše a vedoucí dostanou upozornění se směnami, které odpadají.
                            </p>
                        )}
                    </>
                )}

                {vysledekNemoci && (
                    <div style={{ marginTop: 12 }}>
                        <p className="flex items-center gap-2 font-semibold" style={{ color: 'var(--text-primary)' }}><Check size={18} style={{ color: SECTION_COLOR }} /> Vedoucí o tom ví. Brzy se uzdrav!</p>
                        <p style={popisek}>Odpadají směny · {vysledekNemoci.smeny.length}</p>
                        {vysledekNemoci.smeny.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>V té době žádnou směnu nemáš.</p>}
                        {vysledekNemoci.smeny.map(s => (
                            <p key={s.id} className="text-sm" style={{ color: 'var(--text-primary)', padding: '6px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}`, margin: 0 }}>
                                {new Date(s.datum).toLocaleDateString('cs', { weekday: 'short', day: 'numeric', month: 'numeric' })} · {s.cas_od}–{s.cas_do}
                            </p>
                        ))}
                    </div>
                )}

                {druh === 'vymena' && (
                    <>
                        <p style={popisek}>Kterou směnu</p>
                        {smeny === null && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Načítám…</p>}
                        {smeny?.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>V příštích 30 dnech nemáš směnu, kterou by šlo vyměnit (musí začínat nejdřív za 12 hodin).</p>}
                        {smeny?.map(s => (
                            <button key={s.id} type="button" onClick={() => setSmenaKVymene(s)} className="w-full flex items-center text-left"
                                style={{ gap: 10, minHeight: 50, padding: '6px 12px', borderRadius: 8, border: `1px solid ${hrana}`, background: 'transparent', cursor: 'pointer', marginBottom: 6 }}>
                                <span className="flex-1">
                                    <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {new Date(s.datum).toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'numeric' })}
                                    </span>
                                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
                                        {String(s.cas_od).slice(0, 5)}–{String(s.cas_do).slice(0, 5)}{s.sablon?.nazev ? ` · ${s.sablon.nazev}` : ''}
                                    </span>
                                </span>
                                <Repeat14 />
                            </button>
                        ))}
                    </>
                )}

                {(druh === 'zaloha' || druh === 'nakup' || druh === 'jine') && (
                    <>
                        {druh !== 'zaloha' && (
                            <>
                                <p style={popisek}>O co žádáš</p>
                                <input value={nazev} onChange={e => setNazev(e.target.value)} maxLength={150} style={pole}
                                    placeholder={druh === 'nakup' ? 'Např. nové boty do kuchyně' : 'Např. změna úvazku od listopadu'} />
                            </>
                        )}
                        {(druh === 'zaloha' || druh === 'nakup') && (
                            <>
                                <p style={popisek}>{druh === 'zaloha' ? 'Částka' : 'Přibližná cena (nepovinné)'}</p>
                                <input type="number" inputMode="numeric" min="0" value={castka} onChange={e => setCastka(e.target.value)} style={{ ...pole, maxWidth: 200 }} placeholder="Kč" />
                                {druh === 'zaloha' && castka > 0 && <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>{kc(castka)}</p>}
                            </>
                        )}
                        <p style={popisek}>{druh === 'zaloha' ? 'Kdy ji potřebuješ (nepovinné)' : 'Termín (nepovinné)'}</p>
                        <input type="date" value={datum} onChange={e => setDatum(e.target.value)} style={{ ...pole, maxWidth: 220 }} />
                        <p style={popisek}>{druh === 'jine' ? 'Popis' : 'Poznámka (nepovinné)'}</p>
                        <textarea value={popis} onChange={e => setPopis(e.target.value)} rows={3} style={{ ...pole, height: 'auto', padding: '10px 12px' }} />
                        <PrilohyVyber typ="zadost" prilohy={prilohy} onChange={setPrilohy} onNahravani={setNahravam} isDark={isDark} />
                    </>
                )}
            </div>
        </ModalShell>
    );
}

function Repeat14() {
    const Icon = DRUHY.vymena.Icon;
    return <Icon size={16} style={{ color: SECTION_COLOR }} />;
}
